import React, { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
	netsuiteService,
	PurchaseOrder,
	VendorOption,
	EmployeeOption,
} from "../services/netsuiteService";

const MAX_VENDOR_POS = 5;

export const POList: React.FC = () => {
	const { accounts } = useMsal();
	const isAuthenticated = accounts.length > 0;
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [sortColumn, setSortColumn] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
	const [expandedPOs, setExpandedPOs] = useState<Set<string>>(new Set());
	const [selectedVendor, setSelectedVendor] = useState<string>("");
	const [selectedPONumber, setSelectedPONumber] = useState<string>("");
	const [selectedInternalId, setSelectedInternalId] = useState<string>("");
	const [selectedDateFilter, setSelectedDateFilter] =
		useState<string>("lastMonth");
	const [selectedStatus, setSelectedStatus] = useState<string>("");
	const [showLegacyPOs, setShowLegacyPOs] = useState<boolean>(false);
	const [showMyPendingApprovalPOs, setShowMyPendingApprovalPOs] =
		useState<boolean>(false);
	const [tooltipState, setTooltipState] = useState<{
		visible: boolean;
		poId: string | null;
		reason: string;
	}>({ visible: false, poId: null, reason: "" });

	const [receiveModal, setReceiveModal] = useState<{
		visible: boolean;
		poId: string;
		poDocNumber: string;
	}>({ visible: false, poId: "", poDocNumber: "" });
	const [receiveMemo, setReceiveMemo] = useState<string>("");
	const [receiveDate, setReceiveDate] = useState<string>("");
	const [selectedReceiptLineIds, setSelectedReceiptLineIds] = useState<
		Set<string>
	>(new Set());
	const [receivedItems, setReceivedItems] = useState<
		Array<{ memo: string; amount: string }>
	>([]);
	const [receivingPoId, setReceivingPoId] = useState<string | null>(null);
	const [receiveSubmitting, setReceiveSubmitting] = useState<boolean>(false);
	const [receiveResult, setReceiveResult] = useState<{
		success: boolean;
		message: string;
	} | null>(null);

	const [approvingPoId, setApprovingPoId] = useState<string | null>(null);
	const [approveResult, setApproveResult] = useState<{
		success: boolean;
		message: string;
	} | null>(null);

	const [rejectModal, setRejectModal] = useState<{
		visible: boolean;
		poId: string;
		poDocNumber: string;
	}>({ visible: false, poId: "", poDocNumber: "" });
	const [rejectMessage, setRejectMessage] = useState<string>("");
	const [rejectSubmitting, setRejectSubmitting] = useState<boolean>(false);
	const [rejectResult, setRejectResult] = useState<{
		success: boolean;
		message: string;
	} | null>(null);

	// Tab-based UI state
	const [activeTab, setActiveTab] = useState<"myPOs" | "searchAll">("myPOs");

	// Global search state
	const [vendors, setVendors] = useState<VendorOption[]>([]);
	const [employees, setEmployees] = useState<EmployeeOption[]>([]);
	const [selectedSearchVendor, setSelectedSearchVendor] = useState<string>("");
	const [searchVendorText, setSearchVendorText] = useState<string>("");
	const [showVendorDropdown, setShowVendorDropdown] = useState<boolean>(false);
	const [searchSubmitterName, setSearchSubmitterName] = useState<string>("");
	const [searchEmployeeText, setSearchEmployeeText] = useState<string>("");
	const [showEmployeeDropdown, setShowEmployeeDropdown] =
		useState<boolean>(false);
	const [employeeNameCache, setEmployeeNameCache] = useState<
		Record<string | number, string>
	>({});
	const vendorDropdownRef = useRef<HTMLDivElement>(null);
	const employeeDropdownRef = useRef<HTMLDivElement>(null);

	// Filter vendors based on search text
	const filteredSearchVendors = vendors
		.filter((vendor) => {
			const searchLower = (searchVendorText || "").toLowerCase();
			const name = (vendor.name || "").toLowerCase();
			const address = (vendor.address || "").toLowerCase();
			const city = (vendor.city || "").toLowerCase();
			const state = (vendor.state || "").toLowerCase();
			return (
				name.includes(searchLower) ||
				address.includes(searchLower) ||
				city.includes(searchLower) ||
				state.includes(searchLower)
			);
		})
		.filter(
			(vendor, index, self) =>
				// Deduplicate by vendor ID
				index === self.findIndex((v) => v.id === vendor.id),
		);

	const filteredSearchEmployees = employees
		.filter((employee) => {
			const searchLower = (searchEmployeeText || "").toLowerCase();
			const name = (employee.name || "").toLowerCase();
			const email = (employee.email || "").toLowerCase();
			return name.includes(searchLower) || email.includes(searchLower);
		})
		.filter(
			(employee, index, self) =>
				index === self.findIndex((e) => e.email === employee.email),
		);

	// Handle URL parameters for filtering
	useEffect(() => {
		const poId = searchParams.get("id");
		const reset = searchParams.get("reset");
		const vendorId = searchParams.get("vendorId");
		const internalIdParam = searchParams.get("internalId");

		if (internalIdParam) {
			// Show only the PO with this internal ID, ignore all filters, and auto-expand it
			setSelectedInternalId(internalIdParam);
			setSelectedPONumber("");
			setSelectedVendor("");
			setSelectedDateFilter("all");
			setSelectedStatus("");
			setShowLegacyPOs(true);
			setExpandedPOs(new Set([internalIdParam]));
		} else if (reset === "true") {
			// Reset all filters
			setSelectedInternalId("");
			setSelectedVendor("");
			setSelectedPONumber("");
			setSelectedDateFilter("lastMonth");
			setSelectedStatus("");
			setShowLegacyPOs(false);
			setSortColumn(null);
			setSortDirection("asc");
			setExpandedPOs(new Set());
		} else if (vendorId) {
			// If vendorId parameter exists, clear all local filters
			setSelectedVendor("");
			setSelectedPONumber("");
			setSelectedDateFilter("all");
			setSelectedStatus("");
			setShowLegacyPOs(false);
			setSortColumn(null);
			setSortDirection("asc");
			setExpandedPOs(new Set());
		} else if (poId) {
			// If id parameter exists, set it and clear other filters
			setSelectedPONumber(poId);
			setSelectedVendor("");
			setSelectedDateFilter("all");
			setSelectedStatus("");
			setShowLegacyPOs(false);
		}
	}, [searchParams]);

	useEffect(() => {
		let isMounted = true;

		const loadPurchaseOrders = async () => {
			try {
				setLoading(true);
				setError(null);
				const email = accounts[0]?.username || "";
				console.log("[POList] Loading POs for email:", email);
				const vendorId = searchParams.get("vendorId");
				if (vendorId) {
					console.log("[POList] With vendorId filter:", vendorId);
				}
				const orders = vendorId
					? await netsuiteService.fetchPurchaseOrders(email, { vendorId })
					: await netsuiteService.fetchPurchaseOrders(email);
				console.log(
					"[POList] Raw RESTlet response count:",
					Array.isArray(orders) ? orders.length : 1,
				);
				console.log("[POList] Raw RESTlet response:", orders);
				if (Array.isArray(orders)) {
					console.log(
						"[POList] PO summary:",
						orders.map((po) => ({
							internalId: po.internalId,
							documentNumber: po.documentNumber,
							vendor: po.vendor,
							status: po.status,
							approvalStatus: po.approvalStatus,
						})),
					);
				}
				// Only update state if component is still mounted and we're still on My POs tab
				if (isMounted && activeTab === "myPOs") {
					setPurchaseOrders(orders);
				}
			} catch (err: any) {
				if (isMounted && activeTab === "myPOs") {
					setError(err.message || "Failed to load purchase orders");
					console.error("Error loading purchase orders:", err);
				}
			} finally {
				if (isMounted && activeTab === "myPOs") {
					setLoading(false);
				}
			}
		};

		if (isAuthenticated && activeTab === "myPOs") {
			loadPurchaseOrders();
		}

		return () => {
			isMounted = false;
		};
	}, [isAuthenticated, accounts, searchParams, activeTab]);

	// Load vendors for global search when tab changes
	useEffect(() => {
		const loadSearchFilters = async () => {
			try {
				const [vendorList, employeeList] = await Promise.all([
					netsuiteService.fetchVendors(),
					netsuiteService.fetchEmployees(),
				]);
				setVendors(vendorList);
				setEmployees(employeeList);
			} catch (err) {
				console.error("Error loading search filters:", err);
			}
		};

		if (activeTab === "searchAll") {
			loadSearchFilters();
		}
	}, [activeTab]);

	// Handle click outside vendor/employee dropdowns
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				vendorDropdownRef.current &&
				!vendorDropdownRef.current.contains(event.target as Node)
			) {
				setShowVendorDropdown(false);
			}
			if (
				employeeDropdownRef.current &&
				!employeeDropdownRef.current.contains(event.target as Node)
			) {
				setShowEmployeeDropdown(false);
			}
		};

		if (showVendorDropdown || showEmployeeDropdown) {
			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}
	}, [showVendorDropdown, showEmployeeDropdown]);

	const handleSearchVendorSelect = (vendorId: string, vendorName: string) => {
		setSelectedSearchVendor(vendorId);
		setSearchVendorText(vendorName);
		setShowVendorDropdown(false);
	};

	const handleSearchEmployeeSelect = (employee: EmployeeOption) => {
		setSearchSubmitterName(employee.email);
		setSearchEmployeeText(employee.name);
		setShowEmployeeDropdown(false);
	};

	const fetchEmployeeName = async (employeeId: number | string) => {
		const cacheKey = String(employeeId);
		if (employeeNameCache[cacheKey]) {
			return employeeNameCache[cacheKey];
		}
		try {
			const name = await netsuiteService.fetchEmployeeName(employeeId);
			setEmployeeNameCache((prev) => ({
				...prev,
				[cacheKey]: name || `Employee ${employeeId}`,
			}));
			return name || `Employee ${employeeId}`;
		} catch {
			return `Employee ${employeeId}`;
		}
	};

	// Load POs for global search when vendor or submitter email changes in Search All tab
	useEffect(() => {
		const loadPOsForGlobalSearch = async () => {
			if (activeTab !== "searchAll") return;
			if (!selectedSearchVendor && !searchSubmitterName) {
				// No search criteria, clear the list
				setPurchaseOrders([]);
				setLoading(false);
				return;
			}

			try {
				setLoading(true);
				setError(null);

				let orders;
				if (searchSubmitterName) {
					// Search by employee email (selected from dropdown)
					orders = await netsuiteService.fetchPurchaseOrders(
						searchSubmitterName,
						{ searchAll: "T" },
					);
				} else {
					// Search by vendor only
					const email = accounts[0]?.username || "";
					orders = await netsuiteService.fetchPurchaseOrders(email, {
						vendorId: selectedSearchVendor,
					});
				}

				const ordersList = Array.isArray(orders) ? orders : [orders];
				setPurchaseOrders(ordersList);
			} catch (err: any) {
				setError(
					err.message || "Failed to load purchase orders for global search",
				);
				console.error("Error loading POs for global search:", err);
				setPurchaseOrders([]);
			} finally {
				setLoading(false);
			}
		};

		loadPOsForGlobalSearch();
	}, [activeTab, selectedSearchVendor, searchSubmitterName, accounts]);

	// Fetch employee names for all approval route approvers
	useEffect(() => {
		const fetchMissingEmployeeNames = async () => {
			const employeeIds = new Set<string | number>();

			purchaseOrders.forEach((po) => {
				if (po.approvalRoute) {
					po.approvalRoute.forEach((route) => {
						const userId = route.user;
						const cacheKey = String(userId);
						if (
							userId !== null &&
							userId !== undefined &&
							cacheKey !== "" &&
							!employeeNameCache[cacheKey]
						) {
							employeeIds.add(userId);
						}
					});
				}
			});

			for (const employeeId of employeeIds) {
				await fetchEmployeeName(employeeId);
			}
		};

		if (purchaseOrders.length > 0) {
			fetchMissingEmployeeNames();
		}
	}, [purchaseOrders]);

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			// Toggle direction if clicking the same column
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			// Set new column and default to ascending
			setSortColumn(column);
			setSortDirection("asc");
		}
	};

	const getSortedOrders = () => {
		// First, filter based on active tab
		let filtered = purchaseOrders;

		if (activeTab === "myPOs") {
			// My POs tab - filter by logged-in user's POs
			const userEmail = getUserEmail();
			const normalizedUserEmail = normalizeEmail(userEmail);

			if (showMyPendingApprovalPOs) {
				// In approval mode, show POs awaiting this user's approval,
				// regardless of who submitted the PO.
				filtered = filtered.filter(
					(po) =>
						normalizeEmail(po.nextApproverEmail) === normalizedUserEmail &&
						po.approvalStatus === "Pending Approval",
				);
			} else {
				filtered = filtered.filter((po) => {
					if (!po.poSubmitterEmail) {
						return showLegacyPOs;
					}

					return normalizeEmail(po.poSubmitterEmail) === normalizedUserEmail;
				});
			}

			if (selectedInternalId) {
				return filtered.filter((po) => po.internalId === selectedInternalId);
			}
			if (selectedVendor) {
				filtered = filtered.filter((po) => po.vendor === selectedVendor);
			}
			if (selectedPONumber) {
				filtered = filtered.filter(
					(po) => po.documentNumber === selectedPONumber,
				);
			}
			if (selectedDateFilter !== "all") {
				filtered = filtered.filter((po) => isDateInRange(po.date));
			}
			if (selectedStatus) {
				filtered = filtered.filter(
					(po) => po.approvalStatus === selectedStatus,
				);
			}
		} else {
			// Search All tab

			// Apply vendor filter if selected
			if (selectedSearchVendor) {
				filtered = filtered.filter(
					(po) => po.vendorId === selectedSearchVendor,
				);
			}
		}

		// Then, sort if a sort column is selected
		if (!sortColumn) return filtered;

		const sorted = [...filtered].sort((a, b) => {
			let aValue: any = a[sortColumn as keyof PurchaseOrder];
			let bValue: any = b[sortColumn as keyof PurchaseOrder];

			// Handle numeric values
			if (sortColumn === "amount") {
				aValue = parseFloat(aValue) || 0;
				bValue = parseFloat(bValue) || 0;
			}

			// Handle string comparisons
			if (typeof aValue === "string") {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();
			}

			if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
			if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
			return 0;
		});

		return sorted;
	};

	const getUniqueVendors = (): string[] => {
		const vendors = new Set(purchaseOrders.map((po) => po.vendor));
		return Array.from(vendors).sort();
	};

	const getUniquePONumbers = (): string[] => {
		const poNumbers = new Set(purchaseOrders.map((po) => po.documentNumber));
		return Array.from(poNumbers).sort();
	};

	const getUniqueStatuses = (): string[] => {
		const statuses = new Set(purchaseOrders.map((po) => po.approvalStatus));
		return Array.from(statuses).sort();
	};

	const hasLegacyPOs = (): boolean => {
		return getSortedOrders().some((po) => !po.poSubmitterEmail);
	};

	const hasMyPendingApprovalPOs = (): boolean => {
		const userEmail = getUserEmail();
		const normalizedUserEmail = normalizeEmail(userEmail);
		return purchaseOrders.some(
			(po) =>
				normalizeEmail(po.nextApproverEmail) === normalizedUserEmail &&
				po.approvalStatus === "Pending Approval",
		);
	};

	const getDateRangeStart = (): Date => {
		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

		switch (selectedDateFilter) {
			case "lastWeek":
				return new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
			case "lastMonth":
				return new Date(
					today.getFullYear(),
					today.getMonth() - 1,
					today.getDate(),
				);
			case "lastQuarter":
				return new Date(
					today.getFullYear(),
					today.getMonth() - 3,
					today.getDate(),
				);
			case "lastYear":
				return new Date(
					today.getFullYear() - 1,
					today.getMonth(),
					today.getDate(),
				);
			default:
				return new Date(1970, 0, 1); // Very old date for "All"
		}
	};

	const isDateInRange = (dateString: string): boolean => {
		if (selectedDateFilter === "all") return true;

		try {
			const poDate = new Date(dateString);
			const rangeStart = getDateRangeStart();
			const today = new Date();
			today.setHours(23, 59, 59, 999);

			return poDate >= rangeStart && poDate <= today;
		} catch {
			return false;
		}
	};

	const renderSortIcon = (column: string) => {
		if (sortColumn !== column) return " ↕️";
		return sortDirection === "asc" ? " ↑" : " ↓";
	};

	const getStatusSelectColor = (): string => {
		switch (selectedStatus) {
			case "Pending Approval":
				return "bg-yellow-100 text-yellow-800 border-yellow-300";
			case "Approved":
				return "bg-green-100 text-green-800 border-green-300";
			case "Rejected":
				return "bg-red-100 text-red-800 border-red-300";
			default:
				return "bg-white text-gray-900 border-gray-300";
		}
	};

	const toggleExpand = (internalId: string) => {
		const newExpanded = new Set(expandedPOs);
		if (newExpanded.has(internalId)) {
			newExpanded.delete(internalId);
		} else {
			newExpanded.add(internalId);
		}
		setExpandedPOs(newExpanded);
	};

	const downloadPDF = async (po: PurchaseOrder) => {
		try {
			const blob = await netsuiteService.fetchPOPdf(po.internalId);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `Built-${po.documentNumber}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (err: any) {
			alert(`Failed to download PDF: ${err.message}`);
			console.error("Error downloading PDF:", err);
		}
	};

	const copyPO = (po: PurchaseOrder) => {
		navigate("/create-po", { state: { copyFrom: po } });
	};

	const handleReceivePO = async () => {
		setReceiveSubmitting(true);
		setReceiveResult(null);
		try {
			// Find the current PO
			const currentPO = purchaseOrders.find(
				(po) => po.internalId === receiveModal.poId,
			);
			if (!currentPO || !currentPO.lineItems) {
				setReceiveResult({
					success: false,
					message: "PO not found or has no line items",
				});
				return;
			}

			// Build expenseLines from selected items
			const expenseLines = currentPO.lineItems
				.filter((line) => selectedReceiptLineIds.has(String(line.lineId)))
				.map((line) => ({
					memo: line.memo || "",
					amount: parseFloat(line.amount),
				}));

			if (expenseLines.length === 0) {
				setReceiveResult({
					success: false,
					message: "Please select at least one line item to receive",
				});
				return;
			}

			const result = await netsuiteService.receivePoItems(
				receiveModal.poId,
				expenseLines,
				receiveMemo,
				receiveDate,
			);
			setReceiveResult(result);
		} finally {
			setReceiveSubmitting(false);
		}
	};

	const closeReceiveModal = () => {
		if (receiveResult?.success) {
			window.location.href = "/po-list?reset=true";
			return;
		}
		setReceiveModal({ visible: false, poId: "", poDocNumber: "" });
		setReceiveMemo("");
		setReceiveDate("");
		setSelectedReceiptLineIds(new Set());
		setReceivedItems([]);
		setReceiveResult(null);
		setReceiveSubmitting(false);
	};

	const toggleReceiptLineItem = (lineId: string) => {
		const newSelected = new Set(selectedReceiptLineIds);
		if (newSelected.has(lineId)) {
			newSelected.delete(lineId);
		} else {
			newSelected.add(lineId);
		}
		setSelectedReceiptLineIds(newSelected);
	};

	const isLineItemReceived = (lineItem: any): boolean => {
		return receivedItems.some(
			(received) =>
				received.memo === (lineItem.memo || "") &&
				received.amount === lineItem.amount,
		);
	};

	const handleApprovePO = async (poId: string) => {
		setApprovingPoId(poId);
		setApproveResult(null);
		try {
			const result = await netsuiteService.approvePurchaseOrder(poId);
			setApproveResult(result);
			if (result.success) {
				setTimeout(() => {
					window.location.href = "/po-list?reset=true";
				}, 1500);
			}
		} finally {
			setApprovingPoId(null);
		}
	};

	const getUserEmail = (): string => {
		return accounts[0]?.username || "";
	};

	const normalizeEmail = (value?: string): string =>
		(value || "").trim().toLowerCase();

	const handleRejectPO = async () => {
		setRejectSubmitting(true);
		setRejectResult(null);
		try {
			const result = await netsuiteService.rejectPurchaseOrder(
				rejectModal.poId,
				rejectMessage,
			);
			setRejectResult(result);
			if (result.success) {
				setTimeout(() => {
					window.location.href = "/po-list?reset=true";
				}, 1500);
			}
		} finally {
			setRejectSubmitting(false);
		}
	};

	const closeRejectModal = () => {
		if (rejectResult?.success) {
			window.location.href = "/po-list?reset=true";
			return;
		}
		setRejectModal({ visible: false, poId: "", poDocNumber: "" });
		setRejectMessage("");
		setRejectResult(null);
		setRejectSubmitting(false);
	};

	const canApprovePO = (po: PurchaseOrder): boolean => {
		const userEmail = getUserEmail();
		const normalizedUserEmail = normalizeEmail(userEmail);
		return (
			userEmail !== "" &&
			po.approvalStatus === "Pending Approval" &&
			normalizeEmail(po.nextApproverEmail) === normalizedUserEmail
		);
	};

	const canEditPO = (po: PurchaseOrder): boolean => {
		// Cannot edit if PO has no poSubmitterEmail (legacy PO)
		if (!po.poSubmitterEmail) {
			return false;
		}

		// Allow edit if status is Rejected
		if (po.approvalStatus === "Rejected") {
			return true;
		}

		// Allow edit if Pending Approval and no approvals yet
		if (po.approvalStatus === "Pending Approval") {
			if (!po.approvalRoute || po.approvalRoute.length === 0) {
				return true;
			}
			// Check if any route item has approved: "T"
			const hasApprovals = po.approvalRoute.some(
				(route) => route.approved === "T",
			);
			return !hasApprovals; // Can edit if no approvals found
		}

		// Cannot edit in all other cases
		return false;
	};

	if (!isAuthenticated) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
				<div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full mx-4">
					<h2 className="text-2xl font-bold text-gray-900 mb-4">
						Access Denied
					</h2>
					<p className="text-gray-600">
						Please sign in to view purchase orders.
					</p>
					<a
						href="/"
						className="inline-block mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
					>
						Go to Home
					</a>
				</div>
			</div>
		);
	}
	return (
		<div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-6xl mx-auto">
				{/* Page Header */}
				<div className="mb-8">
					<h1 className="text-4xl font-bold text-gray-900 mb-2">
						Purchase Orders
					</h1>
					<p className="text-gray-600">
						View all your purchase orders from NetSuite
					</p>
				</div>

				{/* Content Card */}
				<div className="bg-white rounded-lg shadow-md p-8">
					{/* Error Message */}
					{error && (
						<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
							<p className="text-red-800 font-semibold">Error</p>
							<p className="text-sm text-red-700 mt-1">{error}</p>
						</div>
					)}

					{/* Vendor Filter Banner */}
					{searchParams.get("vendorId") && (
						<div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
							<div>
								<p className="text-blue-800 font-semibold">
									Viewing POs for:{" "}
									{purchaseOrders[0]?.vendor || "Selected Vendor"}
								</p>
								<p className="text-blue-700 text-sm mt-1">
									Showing up to {MAX_VENDOR_POS} most recent purchase orders
								</p>
							</div>
							<a
								href="/po-list?reset=true"
								className="text-sm text-blue-600 hover:text-blue-800 underline"
							>
								Clear vendor filter
							</a>
						</div>
					)}

					{/* Loading State */}
					{loading ? (
						<div className="flex items-center justify-center py-12">
							<div className="text-center">
								<span className="inline-block animate-spin text-4xl mr-4">
									⏳
								</span>
								<p className="text-gray-600 font-medium">
									Loading purchase orders...
								</p>
							</div>
						</div>
					) : (
						<>
							{/* Tab Navigation */}
							<div className="mb-6 border-b border-gray-200">
								<div className="flex gap-4">
									<button
										onClick={() => {
											setActiveTab("myPOs");
											setSelectedSearchVendor("");
											setSearchVendorText("");
											setSearchSubmitterName("");
											setSearchEmployeeText("");
										}}
										className={`px-4 py-3 font-medium border-b-2 transition ${
											activeTab === "myPOs"
												? "border-blue-600 text-blue-600"
												: "border-transparent text-gray-600 hover:text-gray-900"
										}`}
									>
										My Purchase Orders
									</button>
									<button
										onClick={() => {
											setActiveTab("searchAll");
											setPurchaseOrders([]);
											setSelectedSearchVendor("");
											setSearchVendorText("");
											setSearchSubmitterName("");
											setSearchEmployeeText("");
										}}
										className={`px-4 py-3 font-medium border-b-2 transition ${
											activeTab === "searchAll"
												? "border-blue-600 text-blue-600"
												: "border-transparent text-gray-600 hover:text-gray-900"
										}`}
									>
										Search All POs
									</button>
								</div>
							</div>

							{/* Empty state for My POs - keep tabs visible */}
							{activeTab === "myPOs" && purchaseOrders.length === 0 && (
								<div className="text-center py-8">
									<p className="text-xl text-gray-600 mb-4">
										No purchase orders found
									</p>
									<a
										href="/create-po"
										className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
									>
										Create New PO
									</a>
								</div>
							)}

							{/* Filters - My POs Tab */}
							{activeTab === "myPOs" && (
								<div className="mb-6 flex items-center gap-6 flex-wrap">
									{/* Reset Filters button — shown when viewing a specific PO via URL */}
									{selectedInternalId && (
										<a
											href="/po-list?reset=true"
											className="px-4 py-2 bg-green-100 border border-green-300 text-green-800 text-sm font-medium rounded-lg hover:bg-green-200 transition"
										>
											← Reset Filters
										</a>
									)}
									{/* Vendor Filter */}
									<div className="flex items-center gap-4">
										<label className="text-sm font-semibold text-gray-700">
											Filter by Vendor:
										</label>
										<select
											value={selectedVendor}
											onChange={(e) => setSelectedVendor(e.target.value)}
											className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
										>
											<option value="">All Vendors</option>
											{getUniqueVendors().map((vendor) => (
												<option key={vendor} value={vendor}>
													{vendor}
												</option>
											))}
										</select>
									</div>

									{/* PO Number Filter */}
									<div className="flex items-center gap-4">
										<label className="text-sm font-semibold text-gray-700">
											Filter by PO Number:
										</label>
										<select
											value={selectedPONumber}
											onChange={(e) => setSelectedPONumber(e.target.value)}
											className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
										>
											<option value="">All PO Numbers</option>
											{getUniquePONumbers().map((poNumber) => (
												<option key={poNumber} value={poNumber}>
													{poNumber}
												</option>
											))}
										</select>
									</div>

									{/* Date Filter */}
									<div className="flex items-center gap-4">
										<label className="text-sm font-semibold text-gray-700">
											Filter by Date:
										</label>
										<select
											value={selectedDateFilter}
											onChange={(e) => setSelectedDateFilter(e.target.value)}
											className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
										>
											<option value="all">All</option>
											<option value="lastWeek">Within Last Week</option>
											<option value="lastMonth">Within Last Month</option>
											<option value="lastQuarter">Within Last Quarter</option>
											<option value="lastYear">Within Last Year</option>
										</select>
									</div>

									{/* Status Filter */}
									<div className="flex items-center gap-4">
										<label className="text-sm font-semibold text-gray-700">
											Filter by Status:
										</label>
										<select
											value={selectedStatus}
											onChange={(e) => setSelectedStatus(e.target.value)}
											className={`px-4 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${getStatusSelectColor()}`}
										>
											<option value="">All Statuses</option>
											{getUniqueStatuses().map((status) => (
												<option key={status} value={status}>
													{status}
												</option>
											))}
										</select>
									</div>
								</div>
							)}

							{/* Filters - Search All Tab */}
							{activeTab === "searchAll" && (
								<div className="mb-6 space-y-4">
									<p className="text-xs text-gray-500 italic">
										Search results show purchase orders from the last year
									</p>

									{/* Vendor Search Dropdown */}
									<div ref={vendorDropdownRef}>
										<label className="block text-sm font-semibold text-gray-700 mb-2">
											Search by Vendor
										</label>
										<div className="relative">
											<input
												type="text"
												placeholder="Search vendors..."
												value={searchVendorText}
												autoComplete="off"
												onChange={(e) => {
													setSearchVendorText(e.target.value);
													setShowVendorDropdown(true);
												}}
												onFocus={() => {
													setShowVendorDropdown(true);
												}}
												className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											/>

											{/* Dropdown List */}
											{showVendorDropdown && (
												<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
													{filteredSearchVendors.length > 0 ? (
														filteredSearchVendors.map((vendor) => (
															<button
																key={vendor.id}
																type="button"
																onClick={() =>
																	handleSearchVendorSelect(
																		vendor.id,
																		vendor.name,
																	)
																}
																className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
															>
																<div className="font-medium text-gray-900">
																	{vendor.name}
																</div>
																<div className="text-xs text-gray-500">
																	{vendor.address && vendor.city && vendor.state
																		? `${vendor.address}, ${vendor.city}, ${vendor.state}${vendor.zip ? ` ${vendor.zip}` : ""}`
																		: `ID: ${vendor.id}`}
																</div>
															</button>
														))
													) : (
														<div className="px-4 py-3 text-center text-gray-500">
															No vendors found
														</div>
													)}
												</div>
											)}
										</div>
										{selectedSearchVendor && (
											<button
												onClick={() => {
													setSelectedSearchVendor("");
													setSearchVendorText("");
												}}
												className="mt-2 text-sm text-blue-600 hover:text-blue-800"
											>
												Clear vendor selection
											</button>
										)}
									</div>

									{/* Employee Search Dropdown */}
									<div ref={employeeDropdownRef}>
										<label className="block text-sm font-semibold text-gray-700 mb-2">
											Search by PO Submitter
										</label>
										<div className="relative">
											<input
												type="text"
												placeholder="Search employees by name or email..."
												value={searchEmployeeText}
												autoComplete="off"
												onChange={(e) => {
													setSearchEmployeeText(e.target.value);
													setSearchSubmitterName("");
													setShowEmployeeDropdown(true);
												}}
												onFocus={() => {
													setShowEmployeeDropdown(true);
												}}
												className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
											/>

											{showEmployeeDropdown && (
												<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
													{filteredSearchEmployees.length > 0 ? (
														filteredSearchEmployees.map((employee) => (
															<button
																key={`${employee.email}-${employee.id}`}
																type="button"
																onClick={() =>
																	handleSearchEmployeeSelect(employee)
																}
																className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition"
															>
																<div className="font-medium text-gray-900">
																	{employee.name}
																</div>
																<div className="text-xs text-gray-500">
																	{employee.email}
																</div>
															</button>
														))
													) : (
														<div className="px-4 py-3 text-center text-gray-500">
															No employees found
														</div>
													)}
												</div>
											)}
										</div>
										{searchSubmitterName && (
											<button
												onClick={() => {
													setSearchSubmitterName("");
													setSearchEmployeeText("");
												}}
												className="mt-2 text-sm text-blue-600 hover:text-blue-800"
											>
												Clear employee selection
											</button>
										)}
									</div>

									{/* Clear Filters Button */}
									{(selectedSearchVendor || searchSubmitterName) && (
										<button
											onClick={() => {
												setSelectedSearchVendor("");
												setSearchVendorText("");
												setSearchSubmitterName("");
												setSearchEmployeeText("");
											}}
											className="px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition"
										>
											Clear All Filters
										</button>
									)}
								</div>
							)}

							{/* Toggles Row - Only show on My POs tab */}
							{activeTab === "myPOs" && (
								<div className="mb-6 flex items-center gap-6 flex-wrap">
									{/* Legacy PO Toggle */}
									<div className="flex items-center gap-3">
										<label className="flex items-center cursor-pointer">
											<input
												type="checkbox"
												id="showLegacyPOs"
												checked={showLegacyPOs}
												onChange={(e) => setShowLegacyPOs(e.target.checked)}
												className="sr-only"
											/>
											<div
												className={`relative w-10 h-6 rounded-full transition-colors ${
													showLegacyPOs ? "bg-blue-600" : "bg-gray-300"
												}`}
											>
												<div
													className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
														showLegacyPOs ? "translate-x-4" : ""
													}`}
												></div>
											</div>
										</label>
										<label
											htmlFor="showLegacyPOs"
											className="text-sm font-medium text-gray-700 cursor-pointer"
										>
											Show Legacy POs
										</label>
									</div>

									{/* My Pending Approval POs Toggle - Only show if there are pending POs */}
									{hasMyPendingApprovalPOs() && (
										<div className="flex items-center gap-3">
											<label className="flex items-center cursor-pointer">
												<input
													type="checkbox"
													id="showMyPendingApprovalPOs"
													checked={showMyPendingApprovalPOs}
													onChange={(e) => {
														const checked = e.target.checked;
														setShowMyPendingApprovalPOs(checked);
														if (checked) {
															setSelectedDateFilter("all");
															setShowLegacyPOs(true);
														}
													}}
													className="sr-only"
												/>
												<div
													className={`relative w-10 h-6 rounded-full transition-colors ${
														showMyPendingApprovalPOs
															? "bg-blue-600"
															: "bg-gray-300"
													}`}
												>
													<div
														className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
															showMyPendingApprovalPOs ? "translate-x-4" : ""
														}`}
													></div>
												</div>
											</label>
											<label
												htmlFor="showMyPendingApprovalPOs"
												className="text-sm font-medium text-gray-700 cursor-pointer"
											>
												POs requiring my approval
											</label>
										</div>
									)}
								</div>
							)}

							{/* Legend - Only show on My POs tab if there are legacy POs */}
							{activeTab === "myPOs" && hasLegacyPOs() && (
								<div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
									<p className="text-sm text-orange-800">
										<span className="inline-block w-3 h-3 bg-orange-100 border border-orange-300 rounded mr-2 align-middle"></span>
										<strong>Legacy PO:</strong> Created directly in NetSuite.
										Can no longer be edited. View only.
									</p>
								</div>
							)}

							<div className="overflow-x-auto">
								<table className="min-w-full border-collapse">
									<thead>
										<tr className="bg-gray-100 border-b-2 border-gray-300">
											<th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 w-10">
												Details
											</th>
											<th
												onClick={() => handleSort("date")}
												className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition"
											>
												Date{renderSortIcon("date")}
											</th>
											<th
												onClick={() => handleSort("documentNumber")}
												className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition whitespace-nowrap"
											>
												PO Number{renderSortIcon("documentNumber")}
											</th>
											<th
												onClick={() => handleSort("vendor")}
												className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition whitespace-nowrap"
											>
												Vendor{renderSortIcon("vendor")}
											</th>
											<th
												onClick={() => handleSort("amount")}
												className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition whitespace-nowrap"
											>
												Amount{renderSortIcon("amount")}
											</th>
											{!searchParams.get("vendorId") && (
												<th
													onClick={() => handleSort("nextApprover")}
													className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition whitespace-nowrap"
												>
													Next Approver{renderSortIcon("nextApprover")}
												</th>
											)}
											<th
												onClick={() => handleSort("approvalStatus")}
												className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition whitespace-nowrap"
											>
												Status{renderSortIcon("approvalStatus")}
											</th>
											<th className="px-4 py-3 w-10" />
										</tr>
									</thead>
									<tbody>
										{(() => {
											const orders = getSortedOrders();
											const displayOrders = searchParams.get("vendorId")
												? orders.slice(0, MAX_VENDOR_POS)
												: orders;
											return displayOrders.map((po) => (
												<React.Fragment key={po.internalId}>
													<tr
														className={`border-b border-gray-200 hover:bg-gray-50 transition ${!po.poSubmitterEmail ? "bg-orange-100" : ""}`}
													>
														<td className="px-4 py-4 text-center">
															<button
																onClick={() => toggleExpand(po.internalId)}
																className="text-blue-600 hover:text-blue-800 font-bold text-base"
																title={
																	expandedPOs.has(po.internalId)
																		? "Collapse"
																		: "Expand"
																}
															>
																{expandedPOs.has(po.internalId) ? "▼" : "▶"}
															</button>
														</td>
														<td className="px-6 py-4 text-sm text-gray-900">
															{po.date}
														</td>
														<td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
															<div className="flex items-center gap-2">
																{canEditPO(po) ? (
																	<a
																		href={`/edit-po/${po.internalId}`}
																		className="text-blue-600 hover:text-blue-800 font-medium underline"
																	>
																		{po.documentNumber}
																	</a>
																) : (
																	<span
																		className="text-gray-500 cursor-not-allowed"
																		title="Can no longer be edited"
																	>
																		{po.documentNumber}
																	</span>
																)}
																{po.approvalStatus === "Approved" && (
																	<button
																		onClick={() => downloadPDF(po)}
																		className="text-blue-600 hover:text-blue-800 font-semibold text-base"
																		title="Download PDF"
																	>
																		📥
																	</button>
																)}
															</div>
														</td>
														<td className="px-6 py-4 text-sm text-gray-900">
															{po.vendorId ? (
																<a
																	href={`/po-list?vendorId=${po.vendorId}`}
																	className="text-blue-600 hover:text-blue-800 underline"
																>
																	{po.vendor}
																</a>
															) : (
																po.vendor
															)}
														</td>
														<td className="px-6 py-4 text-sm text-gray-900">
															$
															{parseFloat(po.amount).toLocaleString("en-US", {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															})}
														</td>
														{!searchParams.get("vendorId") && (
															<td className="px-6 py-4 text-sm text-gray-900">
																{po.nextApprover}
															</td>
														)}
														<td className="px-6 py-4 text-sm">
															<div className="relative inline-block">
																<span
																	className={`px-3 py-1 rounded-full text-xs font-semibold cursor-default ${
																		po.approvalStatus === "Pending Approval"
																			? "bg-yellow-100 text-yellow-800"
																			: po.approvalStatus === "Approved"
																				? "bg-green-100 text-green-800"
																				: po.approvalStatus === "Rejected"
																					? "bg-red-100 text-red-800"
																					: "bg-gray-100 text-gray-800"
																	}`}
																	onMouseEnter={() => {
																		if (
																			po.approvalStatus === "Rejected" &&
																			po.rejectionReason
																		) {
																			setTooltipState({
																				visible: true,
																				poId: po.internalId,
																				reason: po.rejectionReason,
																			});
																		}
																	}}
																	onMouseLeave={() =>
																		setTooltipState({
																			visible: false,
																			poId: null,
																			reason: "",
																		})
																	}
																>
																	{po.approvalStatus}
																</span>
																{tooltipState.visible &&
																	tooltipState.poId === po.internalId && (
																		<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50">
																			<div className="font-semibold mb-1">
																				Rejection Reason
																			</div>
																			<div>{tooltipState.reason}</div>
																			<div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
																		</div>
																	)}
															</div>
														</td>
														<td className="px-4 py-4 text-center">
															<button
																onClick={() => copyPO(po)}
																title="Make Copy"
																className="text-gray-400 hover:text-blue-600 transition"
															>
																<svg
																	xmlns="http://www.w3.org/2000/svg"
																	className="w-4 h-4"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																	strokeWidth={2}
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
																	/>
																</svg>
															</button>
														</td>
													</tr>

													{expandedPOs.has(po.internalId) &&
														po.lineItems &&
														po.lineItems.length > 0 && (
															<tr className="border-b border-gray-200 bg-gray-50">
																<td
																	colSpan={searchParams.get("vendorId") ? 6 : 7}
																	className="px-6 py-4"
																>
																	<div className="ml-4">
																		{/* Action Buttons */}
																		<div className="mb-4 flex gap-2">
																			{canApprovePO(po) && (
																				<button
																					onClick={() =>
																						handleApprovePO(po.internalId)
																					}
																					disabled={
																						approvingPoId !== null ||
																						approveResult !== null
																					}
																					className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
																				>
																					{approvingPoId === po.internalId && (
																						<span className="inline-block animate-spin">
																							⏳
																						</span>
																					)}
																					Approve
																				</button>
																			)}

																			{canApprovePO(po) && (
																				<button
																					onClick={() => {
																						setRejectModal({
																							visible: true,
																							poId: po.internalId,
																							poDocNumber: po.documentNumber,
																						});
																						setRejectMessage("");
																						setRejectResult(null);
																					}}
																					disabled={
																						rejectSubmitting ||
																						rejectResult !== null
																					}
																					className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
																				>
																					{rejectSubmitting && (
																						<span className="inline-block animate-spin">
																							⏳
																						</span>
																					)}
																					Reject
																				</button>
																			)}

																			{po.approvalStatus === "Approved" &&
																				(po.status === "Pending Receipt" ||
																					po.status ===
																						"Pending Billing/Partially Received") && (
																					<button
																						onClick={async () => {
																							const today = new Date()
																								.toISOString()
																								.split("T")[0];
																							setReceiveDate(today);
																							setReceiveMemo("");
																							setReceiveResult(null);
																							setReceivingPoId(po.internalId);

																							// Fetch already-received items first
																							try {
																								const received =
																									await netsuiteService.getReceivedItems(
																										po.internalId,
																									);
																								setReceivedItems(received);

																								// Initialize all line items as checked
																								const allLineIds =
																									new Set<string>();
																								po.lineItems?.forEach(
																									(line) => {
																										allLineIds.add(
																											String(line.lineId),
																										);
																									},
																								);
																								setSelectedReceiptLineIds(
																									allLineIds,
																								);

																								// Now open the modal
																								setReceiveModal({
																									visible: true,
																									poId: po.internalId,
																									poDocNumber:
																										po.documentNumber,
																								});
																							} catch (err) {
																								console.error(
																									"Failed to fetch received items:",
																									err,
																								);
																							} finally {
																								setReceivingPoId(null);
																							}
																						}}
																						disabled={
																							receivingPoId === po.internalId
																						}
																						className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition disabled:opacity-70 disabled:cursor-not-allowed"
																					>
																						{receivingPoId === po.internalId ? (
																							<>
																								<svg
																									className="animate-spin h-4 w-4"
																									xmlns="http://www.w3.org/2000/svg"
																									fill="none"
																									viewBox="0 0 24 24"
																								>
																									<circle
																										className="opacity-25"
																										cx="12"
																										cy="12"
																										r="10"
																										stroke="currentColor"
																										strokeWidth="4"
																									/>
																									<path
																										className="opacity-75"
																										fill="currentColor"
																										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
																									/>
																								</svg>
																								Loading...
																							</>
																						) : (
																							"Receive PO"
																						)}
																					</button>
																				)}
																		</div>
																		{/* PO Details Section */}
																		<div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-300 rounded">
																			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
																				{po.location && (
																					<div>
																						<p className="text-xs font-semibold text-gray-600">
																							Location
																						</p>
																						<p className="text-sm text-gray-900 mt-1">
																							{po.location}
																						</p>
																					</div>
																				)}
																				{po.dueDate && (
																					<div>
																						<p className="text-xs font-semibold text-gray-600">
																							Receive by
																						</p>
																						<p className="text-sm text-gray-900 mt-1">
																							{po.dueDate}
																						</p>
																					</div>
																				)}
																				{po.memo && (
																					<div>
																						<p className="text-xs font-semibold text-gray-600">
																							Memo
																						</p>
																						<p className="text-sm text-gray-900 mt-1">
																							{po.memo}
																						</p>
																					</div>
																				)}
																				{po.status && (
																					<div>
																						<p className="text-xs font-semibold text-gray-600">
																							Document Status
																						</p>
																						<p className="mt-1">
																							<span className="inline-block px-2 py-0.5 text-xs font-semibold rounded border bg-indigo-50 border-indigo-300 text-indigo-800">
																								{po.status}
																							</span>
																						</p>
																					</div>
																				)}
																				{(po.poSubmitter ||
																					po.poRequestorName) && (
																					<div>
																						<p className="text-xs font-semibold text-gray-600">
																							Submitted By
																						</p>
																						<p className="text-sm text-gray-900 mt-1">
																							{po.poSubmitter ||
																								po.poRequestorName}
																						</p>
																					</div>
																				)}
																			</div>
																		</div>

																		<h4 className="text-sm font-semibold text-gray-900 mb-3">
																			Line Items
																		</h4>
																		<div className="space-y-2">
																			{po.lineItems.map((line) => (
																				<div
																					key={line.lineId}
																					className="border-l-4 border-blue-300 pl-4 py-2 bg-white rounded"
																				>
																					<div
																						className="grid gap-4 pr-6"
																						style={{
																							gridTemplateColumns:
																								"2fr 1.5fr 1.5fr 1fr",
																						}}
																					>
																						<div>
																							<p className="text-xs font-semibold text-gray-600">
																								Account
																							</p>
																							<p className="text-sm text-gray-900">
																								{line.account || "—"}
																							</p>
																						</div>
																						<div>
																							<p className="text-xs font-semibold text-gray-600">
																								Customer
																							</p>
																							<p className="text-sm text-gray-900">
																								{line.customer || "—"}
																							</p>
																						</div>
																						<div>
																							<p className="text-xs font-semibold text-gray-600">
																								Memo
																							</p>
																							<p className="text-sm text-gray-900">
																								{line.memo || "—"}
																							</p>
																						</div>
																						<div className="text-right">
																							<p className="text-xs font-semibold text-gray-600">
																								Amount
																							</p>
																							<p className="text-sm font-medium text-gray-900">
																								$
																								{parseFloat(
																									line.amount,
																								).toLocaleString("en-US", {
																									minimumFractionDigits: 2,
																									maximumFractionDigits: 2,
																								})}
																							</p>
																						</div>
																					</div>
																				</div>
																			))}
																		</div>

																		{po.approvalRoute &&
																			po.approvalRoute.length > 0 && (
																				<>
																					<h4 className="text-sm font-semibold text-gray-900 mb-3 mt-6">
																						Approval Route
																					</h4>
																					<div className="space-y-2">
																						{po.approvalRoute.map(
																							(route, idx) => {
																								const employeeName =
																									employeeNameCache[
																										route.user
																									] || `Employee ${route.user}`;
																								return (
																									<div
																										key={idx}
																										className={`border-l-4 pl-4 py-2 rounded ${
																											route.approved === "T"
																												? "border-green-300 bg-green-50"
																												: "border-yellow-300 bg-yellow-50"
																										}`}
																									>
																										<div className="flex items-center justify-between">
																											<div>
																												<p className="text-sm font-medium text-gray-900">
																													{employeeName}
																												</p>
																												<p className="text-xs text-gray-500">
																													{route.name}
																												</p>
																												<p className="text-xs text-gray-600 mt-1">
																													{route.approved ===
																													"T"
																														? "✓ Approved"
																														: "⏳ Pending"}
																												</p>
																											</div>
																											{route.approved_on && (
																												<p className="text-xs text-gray-600">
																													{route.approved_on}
																												</p>
																											)}
																										</div>
																									</div>
																								);
																							},
																						)}
																					</div>
																				</>
																			)}
																	</div>
																</td>
															</tr>
														)}
												</React.Fragment>
											));
										})()}
									</tbody>
								</table>
							</div>
						</>
					)}
				</div>
			</div>

			{/* Approve Result Notification */}
			{approveResult && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
					<div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
						<div
							className={`p-4 rounded-lg mb-5 ${
								approveResult.success
									? "bg-green-50 border border-green-200"
									: "bg-red-50 border border-red-200"
							}`}
						>
							<p
								className={`font-semibold ${
									approveResult.success ? "text-green-800" : "text-red-800"
								}`}
							>
								{approveResult.success ? "Success!" : "Error"}
							</p>
							<p
								className={`text-sm mt-1 ${
									approveResult.success ? "text-green-700" : "text-red-700"
								}`}
							>
								{approveResult.message}
							</p>
						</div>
						<div className="flex justify-end">
							<button
								onClick={() => setApproveResult(null)}
								className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Reject PO Modal */}
			{rejectModal.visible && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
					<div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
						<h2 className="text-xl font-bold text-gray-900 mb-1">Reject PO</h2>
						<p className="text-sm text-gray-500 mb-5">
							PO Number:{" "}
							<span className="font-medium text-gray-700">
								{rejectModal.poDocNumber}
							</span>
						</p>

						{rejectResult ? (
							<div>
								<div
									className={`p-4 rounded-lg mb-5 ${
										rejectResult.success
											? "bg-green-50 border border-green-200"
											: "bg-red-50 border border-red-200"
									}`}
								>
									<p
										className={`font-semibold ${
											rejectResult.success ? "text-green-800" : "text-red-800"
										}`}
									>
										{rejectResult.success ? "Success!" : "Error"}
									</p>
									<p
										className={`text-sm mt-1 ${
											rejectResult.success ? "text-green-700" : "text-red-700"
										}`}
									>
										{rejectResult.message}
									</p>
								</div>
								<div className="flex justify-end">
									<button
										onClick={closeRejectModal}
										className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
									>
										Close
									</button>
								</div>
							</div>
						) : (
							<div>
								<div className="mb-5">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Rejection Reason <span className="text-red-600">*</span>
									</label>
									<textarea
										value={rejectMessage}
										onChange={(e) => setRejectMessage(e.target.value)}
										placeholder="Enter the reason for rejecting this PO"
										disabled={rejectSubmitting}
										rows={4}
										className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none ${
											!rejectMessage.trim()
												? "border-red-300 bg-red-50"
												: "border-gray-300"
										}`}
									/>
									{!rejectMessage.trim() && (
										<p className="mt-1 text-xs text-red-600">
											A rejection reason is required.
										</p>
									)}
								</div>
								<div className="flex gap-3 justify-end">
									<button
										onClick={closeRejectModal}
										disabled={rejectSubmitting}
										className="px-5 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
									>
										Cancel
									</button>
									<button
										onClick={handleRejectPO}
										disabled={rejectSubmitting || !rejectMessage.trim()}
										className="px-5 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
									>
										{rejectSubmitting && (
											<span className="inline-block animate-spin">⏳</span>
										)}
										Reject
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Receive PO Modal */}
			{receiveModal.visible && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
					<div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
						<h2 className="text-xl font-bold text-gray-900 mb-1">Receive PO</h2>
						<p className="text-sm text-gray-500 mb-5">
							PO Number:{" "}
							<span className="font-medium text-gray-700">
								{receiveModal.poDocNumber}
							</span>
						</p>

						{receiveResult ? (
							<div>
								<div
									className={`p-4 rounded-lg mb-5 ${
										receiveResult.success
											? "bg-green-50 border border-green-200"
											: "bg-red-50 border border-red-200"
									}`}
								>
									<p
										className={`font-semibold ${
											receiveResult.success ? "text-green-800" : "text-red-800"
										}`}
									>
										{receiveResult.success ? "Success!" : "Error"}
									</p>
									<p
										className={`text-sm mt-1 ${
											receiveResult.success ? "text-green-700" : "text-red-700"
										}`}
									>
										{receiveResult.message}
									</p>
								</div>
								<div className="flex justify-end">
									<button
										onClick={closeReceiveModal}
										className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
									>
										Close
									</button>
								</div>
							</div>
						) : (
							<div>
								<div className="mb-5">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Receipt Date
									</label>
									<input
										type="date"
										value={receiveDate}
										onChange={(e) => setReceiveDate(e.target.value)}
										disabled={receiveSubmitting}
										className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
									/>
								</div>
								<div className="mb-5">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Memo
									</label>
									<input
										type="text"
										value={receiveMemo}
										onChange={(e) => setReceiveMemo(e.target.value)}
										placeholder="Enter memo (optional)"
										disabled={receiveSubmitting}
										className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
									/>
								</div>

								{/* Line Items */}
								<div className="mb-5">
									<div className="flex justify-between items-center mb-3">
										<label className="block text-sm font-medium text-gray-700">
											Select Items to Receive{" "}
											<span className="text-red-600">*</span>
										</label>
										<button
											onClick={() => {
												const po = purchaseOrders.find(
													(p) => p.internalId === receiveModal.poId,
												);
												if (!po?.lineItems) return;

												const allLineIds = new Set<string>();
												const hasAllSelected =
													selectedReceiptLineIds.size === po.lineItems.length;

												if (!hasAllSelected) {
													// Check all
													po.lineItems.forEach((line) => {
														allLineIds.add(String(line.lineId));
													});
												}
												// If already all selected, uncheck all (empty set)
												setSelectedReceiptLineIds(allLineIds);
											}}
											className="px-3 py-1 text-xs font-semibold bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
										>
											{selectedReceiptLineIds.size ===
											purchaseOrders.find(
												(p) => p.internalId === receiveModal.poId,
											)?.lineItems?.length
												? "Uncheck All"
												: "Check All"}
										</button>
									</div>
									<div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-gray-50">
										{purchaseOrders
											.find((po) => po.internalId === receiveModal.poId)
											?.lineItems?.map((line) => {
												const isReceived = isLineItemReceived(line);
												return (
													<div
														key={line.lineId}
														className={`flex items-center gap-3 p-2 rounded transition ${
															isReceived
																? "bg-gray-200 opacity-60"
																: "hover:bg-white"
														}`}
													>
														<input
															type="checkbox"
															id={`line-${line.lineId}`}
															checked={
																isReceived ||
																selectedReceiptLineIds.has(String(line.lineId))
															}
															onChange={() =>
																!isReceived &&
																toggleReceiptLineItem(String(line.lineId))
															}
															disabled={receiveSubmitting || isReceived}
															className="w-4 h-4 rounded cursor-pointer"
														/>
														<label
															htmlFor={`line-${line.lineId}`}
															className={`flex-1 ${
																isReceived
																	? "cursor-not-allowed"
																	: "cursor-pointer"
															}`}
														>
															<div className="flex justify-between items-start">
																<div>
																	<span
																		className={`text-sm ${
																			isReceived
																				? "line-through text-gray-500"
																				: "text-gray-700"
																		}`}
																	>
																		{line.memo || "No memo"}
																	</span>
																	{isReceived && (
																		<span className="text-xs text-gray-500 ml-2">
																			(Already Received)
																		</span>
																	)}
																</div>
																<span className="text-sm font-semibold text-gray-900 ml-4">
																	$
																	{parseFloat(line.amount).toLocaleString(
																		"en-US",
																		{
																			minimumFractionDigits: 2,
																			maximumFractionDigits: 2,
																		},
																	)}
																</span>
															</div>
														</label>
													</div>
												);
											})}
										{!purchaseOrders.find(
											(po) => po.internalId === receiveModal.poId,
										)?.lineItems?.length && (
											<p className="text-sm text-gray-500">
												No line items available
											</p>
										)}
									</div>
									{selectedReceiptLineIds.size === 0 && (
										<p className="mt-1 text-xs text-red-600">
											Please select at least one item to receive.
										</p>
									)}
								</div>

								<div className="flex gap-3 justify-end">
									<button
										onClick={closeReceiveModal}
										disabled={receiveSubmitting}
										className="px-5 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
									>
										Cancel
									</button>
									<button
										onClick={handleReceivePO}
										disabled={
											receiveSubmitting || selectedReceiptLineIds.size === 0
										}
										className="px-5 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
									>
										{receiveSubmitting && (
											<span className="inline-block animate-spin">⏳</span>
										)}
										{receiveSubmitting ? "Submitting..." : "Receive PO"}
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};
